import { isDefined, isNotDefined } from '@sgme/fp';

import { getExecution } from '@/store/growl/slice.ts';
import type { DealExecutionId } from '@/store/growl/state/common.ts';
import type { GetAppState } from '@/store/store.ts';

const resolversByGrowlId = new Map<DealExecutionId, (() => void)[]>();

/**
 * Resolves when the growl of an execution is closed, by the user or by auto-close.
 *
 * Used to keep the `ReceiveFillReport` listener of an execution subscribed beyond the lifetime of
 * its RTK Query cache entry: the back office can send the trade id long after the RFS is closed,
 * and the user is still waiting for it as long as the growl is on screen.
 *
 * Resolves immediately when the growl is already gone, so a caller can always await it safely.
 */
export function whenGrowlClosed(growlId: DealExecutionId, getState: GetAppState): Promise<void> {
  if (isNotDefined(getExecution(getState(), growlId))) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const resolvers = resolversByGrowlId.get(growlId);

    if (isDefined(resolvers)) {
      resolvers.push(resolve);
      return;
    }

    resolversByGrowlId.set(growlId, [resolve]);
  });
}

/** called by the growl listeners when a growl is removed from the store */
export function notifyGrowlClosed(growlId: DealExecutionId): void {
  const resolvers = resolversByGrowlId.get(growlId);

  if (isNotDefined(resolvers)) {
    return;
  }

  resolversByGrowlId.delete(growlId);

  for (const resolve of resolvers) {
    resolve();
  }
}
